import { useMutation } from "convex/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import { Button } from "#/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "#/components/ui/dialog";
import { Textarea } from "#/components/ui/textarea";
import { toast } from "#/lib/toast";
import { cn } from "#/lib/utils";

const DESC_MIN = 10;
const DESC_MAX = 1000;

type ReportType = "bug" | "feedback";

export function AppReportDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
}) {
	const { t } = useTranslation("common");
	const submit = useMutation(api.appReports.submit);

	const [type, setType] = useState<ReportType | null>(null);
	const [description, setDescription] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [attempted, setAttempted] = useState(false);

	function reset() {
		setType(null);
		setDescription("");
		setAttempted(false);
	}

	function handleOpenChange(v: boolean) {
		if (!v) reset();
		onOpenChange(v);
	}

	async function handleSubmit() {
		setAttempted(true);
		if (!type || description.length < DESC_MIN || description.length > DESC_MAX)
			return;
		setSubmitting(true);
		const pageUrl = typeof window !== "undefined" ? window.location.href : "";
		try {
			await submit({ type, description: description.trim(), pageUrl });
			handleOpenChange(false);
			toast.success(t("appReport.thanks"));
		} catch {
			toast.error(t("appReport.failed"));
		} finally {
			setSubmitting(false);
		}
	}

	const canSubmit =
		type !== null &&
		description.length >= DESC_MIN &&
		description.length <= DESC_MAX &&
		!submitting;

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("appReport.title")}</DialogTitle>
					<DialogDescription>{t("appReport.subtitle")}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-5 py-1">
					<div className="flex gap-2">
						{(["bug", "feedback"] as const).map((kind) => (
							<button
								key={kind}
								type="button"
								onClick={() => setType(kind)}
								className={cn(
									"flex-1 rounded-md border py-2 text-sm font-medium transition-colors",
									type === kind
										? "border-primary bg-primary/10 text-primary-accessible"
										: "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
								)}
							>
								{kind === "bug"
									? t("appReport.typeBug")
									: t("appReport.typeFeedback")}
							</button>
						))}
					</div>
					{attempted && !type && (
						<p className="text-xs text-destructive -mt-3">
							{t("appReport.errorNoType")}
						</p>
					)}

					<div className="flex flex-col gap-1.5">
						<Textarea
							placeholder={t("appReport.placeholder")}
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							maxLength={DESC_MAX}
							rows={4}
							className="resize-none"
						/>
						<p
							className={cn(
								"text-xs text-right tabular-nums",
								description.length > DESC_MAX
									? "text-destructive"
									: "text-muted-foreground",
							)}
						>
							{description.length}/{DESC_MAX}
						</p>
						{attempted && description.length < DESC_MIN && (
							<p className="text-xs text-destructive">
								{t("appReport.errorTooShort", { min: DESC_MIN })}
							</p>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={submitting}
					>
						{t("appReport.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{submitting ? t("appReport.submitting") : t("appReport.submit")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
